/**
 * ReportFilterPopover — the "Advanced Filter" counterpart to Dedrone's
 * slide-over filter pane.
 *
 * The form is controlled locally and only commits to the parent on Apply,
 * so the user can experiment without triggering queries on every keystroke.
 * Reset clears the form back to "no filters" but keeps the date range,
 * which is managed separately at the page level.
 */
import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import type { ReportFilterInput, Severity } from '../../services/reportsApi';

interface GeofenceOption { id: string; name: string; }
interface SensorOption { id: string; name: string; }

interface Props {
  open: boolean;
  onClose: () => void;
  value: ReportFilterInput;
  onApply: (next: ReportFilterInput) => void;
  geofences?: GeofenceOption[];
  sensors?: SensorOption[];
}

const SEVERITY_OPTIONS: Severity[] = ['INFO', 'WARNING', 'CRITICAL'];
const CLASSIFICATION_OPTIONS = ['UNKNOWN', 'DRONE', 'BIRD', 'AIRCRAFT', 'VEHICLE', 'NOISE'];

export default function ReportFilterPopover({
  open,
  onClose,
  value,
  onApply,
  geofences = [],
  sensors = [],
}: Props) {
  // Work on a local copy so a cancel reverts cleanly.
  const [draft, setDraft] = useState<ReportFilterInput>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const update = <K extends keyof ReportFilterInput>(k: K, v: ReportFilterInput[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const handleReset = () => {
    // Preserve range; clear everything else.
    setDraft({ from: value.from, to: value.to });
  };

  const handleApply = () => {
    // Strip empty strings — the API treats them as "ignore".
    const cleaned: ReportFilterInput = { from: draft.from, to: draft.to };
    for (const [k, v] of Object.entries(draft)) {
      if (k === 'from' || k === 'to') continue;
      if (v !== '' && v !== undefined && v !== null) {
        (cleaned as Record<string, unknown>)[k] = v;
      }
    }
    onApply(cleaned);
    onClose();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="w-full max-w-md h-full bg-navy-900 border-l border-navy-700 shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-5 border-b border-navy-700">
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 text-accent" />
                    <Dialog.Title className="text-base font-semibold text-white">
                      Advanced Filter
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-800 rounded-md"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  <Field label="Alert Type">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      placeholder="e.g. GEOFENCE_BREACH"
                      value={draft.alertType ?? ''}
                      onChange={(e) => update('alertType', e.target.value)}
                    />
                  </Field>

                  <Field label="Severity">
                    <select
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.severity ?? ''}
                      onChange={(e) =>
                        update('severity', (e.target.value || undefined) as Severity | undefined)
                      }
                    >
                      <option value="">Any</option>
                      {SEVERITY_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Zone / Geofence">
                    <select
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.geofenceId ?? ''}
                      onChange={(e) => update('geofenceId', e.target.value || undefined)}
                    >
                      <option value="">Any</option>
                      {geofences.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Sensor">
                    <select
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.sensorId ?? ''}
                      onChange={(e) => update('sensorId', e.target.value || undefined)}
                    >
                      <option value="">Any</option>
                      {sensors.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Classification">
                    <select
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.classification ?? ''}
                      onChange={(e) => update('classification', e.target.value || undefined)}
                    >
                      <option value="">Any</option>
                      {CLASSIFICATION_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Min Confidence (0–1)">
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      step={0.05}
                      min={0}
                      max={1}
                      value={draft.minConfidence ?? ''}
                      onChange={(e) =>
                        update(
                          'minConfidence',
                          e.target.value === '' ? undefined : Number(e.target.value),
                        )
                      }
                    />
                  </Field>

                  <Field label="Model">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.model ?? ''}
                      onChange={(e) => update('model', e.target.value)}
                    />
                  </Field>

                  <Field label="Manufacturer">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.manufacturer ?? ''}
                      onChange={(e) => update('manufacturer', e.target.value)}
                    />
                  </Field>

                  <Field label="Serial Number">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white font-mono placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      value={draft.serialNumber ?? ''}
                      onChange={(e) => update('serialNumber', e.target.value)}
                    />
                  </Field>

                  <Field label="Search Text (title / message)">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                      placeholder="breach, unauthorized, ..."
                      value={draft.searchText ?? ''}
                      onChange={(e) => update('searchText', e.target.value)}
                    />
                  </Field>

                  {/* Phase 5C — altitude bounds (feet, MSL) and a
                      "geofence breaches only" shortcut. The two altitude
                      inputs share a row to suggest they're a range. */}
                  <div>
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1 block">
                      Altitude (ft MSL)
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                        placeholder="Min"
                        min={0}
                        max={60000}
                        step={50}
                        value={draft.minAltitudeFt ?? ''}
                        onChange={(e) =>
                          update(
                            'minAltitudeFt',
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                      <input
                        type="number"
                        className="w-full px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
                        placeholder="Max"
                        min={0}
                        max={60000}
                        step={50}
                        value={draft.maxAltitudeFt ?? ''}
                        onChange={(e) =>
                          update(
                            'maxAltitudeFt',
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-navy-600 bg-navy-800 text-accent focus:ring-accent/30"
                      checked={!!draft.geofenceBreachOnly}
                      onChange={(e) =>
                        update('geofenceBreachOnly', e.target.checked || undefined)
                      }
                    />
                    <span className="text-sm">
                      <span className="text-white font-medium">
                        Geofence breaches only
                      </span>
                      <span className="block text-xs text-gray-500">
                        Restrict to alerts with type GEOFENCE_BREACH.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="flex items-center justify-between gap-2 p-4 border-t border-navy-700">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="btn-secondary"
                  >
                    Reset
                  </button>
                  <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">
                      Cancel
                    </button>
                    <button type="button" onClick={handleApply} className="btn-primary">
                      Apply
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
